/**
 * Tool Executor Component
 * Handles tool execution with input form and result display
 */

import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material'
import {
  PlayArrow as RunIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material'
import { apiClient } from '../../../../services/api'
import BaseDialog from '../../../../components/shared/dialogs/BaseDialog'

interface ToolExecutorProps {
  tool: {
    id: string
    name: string
    description: string
    requiresInput: boolean
    inputSchema?: any
  }
  fpCode: string
  open: boolean
  onClose: () => void
  onExecutionComplete?: () => void
}

const ToolExecutor: React.FC<ToolExecutorProps> = ({ 
  tool, 
  fpCode, 
  open, 
  onClose, 
  onExecutionComplete 
}) => {
  const [executing, setExecuting] = useState(false)
  const [inputValues, setInputValues] = useState<Record<string, any>>({})
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const handleDownloadResult = () => {
    if (!result) return

    // Check if result has export data with specific format
    if (result.export_data && result.export_format) {
      let blob: Blob
      let filename: string
      
      if (result.export_format === 'excel') {
        // For Excel, the data should be base64 encoded
        const binaryString = atob(result.export_data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        filename = result.filename || `${tool.id}_export_${new Date().getTime()}.xlsx`
      } else {
        // Default to JSON
        blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' })
        filename = `${tool.id}_result_${new Date().getTime()}.json`
      }
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } else {
      // Default JSON download for regular results
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${tool.id}_result_${new Date().getTime()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const handleExecute = async () => {
    try {
      setExecuting(true)
      setError(null)
      setResult(null)

      // Special handling for tools with real backend implementations
      if (tool.id === 'duplicate_sales') {
        // Call the duplicate sales endpoint directly
        const response = await apiClient.get(
          `/inspector/sales/tools/duplicate-sales/${fpCode}`
        )
        setResult({
          data: response.data,
          summary: `Found ${response.data.total || 0} duplicate sale groups`,
          message: 'Analysis complete'
        })
      } else if (tool.id === 'cogs_review') {
        // Call the COGS review endpoint directly
        const response = await apiClient.get(
          `/inspector/cogs-account-review/${fpCode}`
        )
        setResult({
          data: response.data,
          summary: 'COGS account review completed',
          message: 'Review the Cost of Goods Sold account mappings'
        })
      } else {
        // For other tools, use the generic execute endpoint (though most are removed now)
        const response = await apiClient.post(
          `/inspector/${fpCode}/tools/${tool.id}/execute`,
          tool.requiresInput || tool.requires_params ? inputValues : {}
        )
        setResult(response.data)
      }

      if (onExecutionComplete) {
        onExecutionComplete()
      }
    } catch (err: any) {
      console.error('Tool execution error:', err)
      setError(err.response?.data?.detail || 'Failed to execute tool')
    } finally {
      setExecuting(false)
    }
  }

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValues(prev => ({
      ...prev,
      [field]: event.target.value
    }))
  }

  const handleClose = () => {
    setInputValues({})
    setResult(null)
    setError(null)
    onClose()
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={tool.name}
      size="md"
      footer={
        <>
          {result && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadResult}
              variant={result.export_format ? 'contained' : 'text'}
              color={result.export_format ? 'secondary' : 'inherit'}
            >
              {result.export_format ? `Download ${result.export_format.toUpperCase()}` : 'Download Result'}
            </Button>
          )}
          {!result && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RunIcon />}
              onClick={handleExecute}
              disabled={executing}
            >
              Execute
            </Button>
          )}
          <Button onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
        </>
      }
    >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {tool.description}
        </Typography>

        {/* Input Form */}
        {tool.requiresInput && tool.inputSchema && !result && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Configuration
            </Typography>
            <Box sx={{ mb: 3 }}>
              {Object.entries(tool.inputSchema).map(([field, schema]: [string, any]) => (
                <TextField
                  key={field}
                  label={schema.label || field}
                  helperText={schema.description}
                  required={schema.required}
                  fullWidth
                  margin="normal"
                  value={inputValues[field] || ''}
                  onChange={handleInputChange(field)}
                  disabled={executing}
                  type={schema.type === 'number' ? 'number' : 'text'}
                />
              ))}
            </Box>
          </>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Execution Progress */}
        {executing && (
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Executing tool...
            </Typography>
          </Box>
        )}

        {/* Result Display */}
        {result && !executing && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <SuccessIcon color="success" />
              <Typography variant="subtitle1" color="success.main">
                Execution Complete
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
              {result.summary && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {result.summary}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {result.data && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Result Data
                  </Typography>
                  <Box 
                    component="pre" 
                    sx={{ 
                      overflow: 'auto',
                      maxHeight: 300,
                      fontSize: '0.875rem',
                      fontFamily: 'monospace'
                    }}
                  >
                    {typeof result.data === 'object' 
                      ? JSON.stringify(result.data, null, 2)
                      : result.data
                    }
                  </Box>
                </Box>
              )}

              {result.export_format && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="success.main">
                    Export ready - Click "Download Result" to save the {result.export_format.toUpperCase()} file
                  </Typography>
                </Box>
              )}

              {result.message && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {result.message}
                </Typography>
              )}
            </Paper>
          </>
        )}
    </BaseDialog>
  )
}

export default ToolExecutor